import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';

export enum Criteria {
  at_least_eight_chars,
  at_least_one_lower_case_char,
  at_least_one_upper_case_char,
  at_least_one_digit_char,
  at_least_one_special_char,
}

export interface StrengthChange {
  strengthString: string;
  isAllChecksPass: boolean;
}

@Component({
  selector: 'app-password-strength',
  templateUrl: './password-strength.component.html',
  styleUrls: ['./password-strength.component.scss'],
  standalone: true,
  imports: [RktIconComponent],
})
export class PasswordStrengthComponent implements OnChanges {
  @Input() password?: string;

  /** Minimum allowed password length. Defaults to 8 to preserve existing consumers. */
  @Input() minLength = 8;

  /** Maximum allowed password length. Defaults to 63 to preserve existing consumers. */
  @Input() maxLength = 63;

  /** When true, whitespace is disallowed and shown as an additional requirement (PCI expiry flow). */
  @Input() disallowSpaces = false;

  @Output() strengthChanged: EventEmitter<StrengthChange> = new EventEmitter<StrengthChange>();

  validators: Criteria[] = Object.keys(Criteria).map((key) => Criteria[key as unknown as number] as unknown as Criteria);

  criteriaMap = new Map<Criteria, RegExp>();

  isValidLength = false;

  containAtLeastOneLowerCaseLetter = false;

  containAtLeastOneUpperCaseLetter = false;

  containAtLeastOneDigit = false;

  containAtLeastOneSpecialChar = false;

  containNoSpaces = false;

  passwordFormControl: AbstractControl;

  private strength = 0;

  constructor() {
    this.criteriaMap.set(Criteria.at_least_eight_chars, RegExp(/^.{8,63}$/));
    this.criteriaMap.set(Criteria.at_least_one_lower_case_char, RegExp(/^(?=.*?[a-z])/));
    this.criteriaMap.set(Criteria.at_least_one_upper_case_char, RegExp(/^(?=.*?[A-Z])/));
    this.criteriaMap.set(Criteria.at_least_one_digit_char, RegExp(/^(?=.*?\d)/));
    this.criteriaMap.set(Criteria.at_least_one_special_char, RegExp(/^(?=.*?[" !#$%&'()*+,-./:;<=>?@[\]^_`{|}~])/));

    this.passwordFormControl = new UntypedFormControl('', [
      ...this.validators.map((criteria) => Validators.pattern(this.getCriteriaMapItem(criteria))),
    ]);
  }

  ngOnChanges(): void {
    if (this.password && this.password.length > 0) {
      this.calculatePasswordStrength();
    } else {
      this.reset();
    }
  }

  calculatePasswordStrength(): void {
    const requirements: boolean[] = [
      this.isValidPasswordLength(),
      this.isContainAtLeastOneLowerCaseLetter(),
      this.isContainAtLeastOneUpperCaseLetter(),
      this.isContainAtLeastOneSpecialChar(),
      this.isContainAtLeastOneDigit(),
    ];

    if (this.disallowSpaces) {
      requirements.push(this.isContainNoSpaces());
    }

    const unit = 100 / requirements.length;
    const passedRequirements = requirements.filter((v) => v).length;
    this.strength = passedRequirements * unit;
    this.strengthChanged.emit({ strengthString: this.strengthString, isAllChecksPass: passedRequirements === requirements.length });
  }

  reset() {
    this.strength = 0;
    this.isValidLength = false;
    this.containAtLeastOneLowerCaseLetter = false;
    this.containAtLeastOneUpperCaseLetter = false;
    this.containAtLeastOneDigit = false;
    this.containAtLeastOneSpecialChar = false;
    this.containNoSpaces = false;

    this.calculatePasswordStrength();
  }

  get strengthString(): string {
    if (this.strength > 0 && this.strength <= 40) {
      return 'Poor';
    } else if (this.strength > 40 && this.strength <= 80) {
      return 'Average';
    } else if (this.strength > 80) {
      return 'Good';
    }
    return '';
  }

  private isValidPasswordLength(): boolean {
    const length = this.password?.length ?? 0;
    this.isValidLength = length >= this.minLength && length <= this.maxLength;
    return this.isValidLength;
  }

  private isContainNoSpaces(): boolean {
    this.containNoSpaces = this.password ? !/\s/.test(this.password) : false;
    return this.containNoSpaces;
  }

  private isContainAtLeastOneLowerCaseLetter(): boolean {
    this.containAtLeastOneLowerCaseLetter = this.password
      ? this.getCriteriaMapItem(Criteria.at_least_one_lower_case_char).test(this.password)
      : false;
    return this.containAtLeastOneLowerCaseLetter;
  }

  private isContainAtLeastOneUpperCaseLetter(): boolean {
    this.containAtLeastOneUpperCaseLetter = this.password
      ? this.getCriteriaMapItem(Criteria.at_least_one_upper_case_char).test(this.password)
      : false;
    return this.containAtLeastOneUpperCaseLetter;
  }

  private isContainAtLeastOneDigit(): boolean {
    this.containAtLeastOneDigit = this.password ? this.getCriteriaMapItem(Criteria.at_least_one_digit_char).test(this.password) : false;
    return this.containAtLeastOneDigit;
  }

  private isContainAtLeastOneSpecialChar(): boolean {
    this.containAtLeastOneSpecialChar = this.password
      ? this.getCriteriaMapItem(Criteria.at_least_one_special_char).test(this.password)
      : false;
    return this.containAtLeastOneSpecialChar;
  }

  private getCriteriaMapItem(criteria: Criteria): RegExp {
    return this.criteriaMap.get(criteria) as RegExp;
  }
}
